import React from "react";
import ContentPasteGoOutlined from "@mui/icons-material/ContentPasteGoOutlined";
import { Box, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import FlowActionButton from "@components/FlowActionButtons/FlowActionButton";
import type { SequenceView } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { executionActions } from "../utils/executionActions";
import { useExecution } from "./executionContext";

const LANG_BASE = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences`;
const L = `${LANG_BASE}.execution.actions`;

const SequenceExecutionButtons: React.FC<{
  sequence: SequenceView;
  operationStatus: string | null;
}> = ({ sequence, operationStatus }) => {
  const { t } = useTranslation();
  const { open } = useExecution();
  const actions = executionActions(sequence, operationStatus);
  const flowButton = (
    labelKey: string,
    testId: string,
    isActionAvailable: boolean,
    onClick: () => void,
  ) => (
    <FlowActionButton
      langBase={LANG_BASE}
      labelKey={`execution.actions.${labelKey}`}
      testId={testId}
      variant="outlined"
      isActionAvailable={isActionAvailable}
      startIcon={<ContentPasteGoOutlined />}
      onClick={onClick}
    />
  );

  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
      {flowButton("equipment", "sequence-details-equipment", actions.equipment, () =>
        open("equipment", sequence),
      )}
      {flowButton("start", "sequence-details-start", actions.start, () => open("start", sequence))}
      {actions.startBlocked && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Button
            variant="outlined"
            disabled
            testId="sequence-details-start"
            startIcon={<ContentPasteGoOutlined />}
            label={<span className="normal-case font-medium">{t(`${L}.start`)}</span>}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="sequence-details-start-blocked"
          >
            {t(`${L}.start-blocked.${actions.startBlocked}`)}
          </Typography>
        </Box>
      )}
      {flowButton("add", "sequence-details-add", actions.add, () => open("add", sequence))}
      {flowButton("complete", "sequence-details-complete", actions.complete !== null, () =>
        open("complete", sequence, actions.complete ?? undefined),
      )}
    </Box>
  );
};

export default SequenceExecutionButtons;
